import { useState } from "react";
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import TelegramIcon from "@mui/icons-material/Telegram";
import WhatsApp from "@mui/icons-material/WhatsApp";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";

import { TawkChatDrawer } from "./TawkChatDrawer";
import { LabeledAction } from "./LabeledAction";
import { mobileFullWidthMenuPaperSx } from "@/theme/mobile-menu-sx";

/**
 * Live chat entry-point — opens a small menu of instant-support channels:
 *   • In-app chat (Tawk.to, renders as a right-side Drawer — matches AI panel)
 *   • Telegram
 *   • WhatsApp
 *
 * The trigger button carries a continuously animated mint dot + ring-ping
 * so it reads as "live" in peripheral vision, replacing the GIF the
 * product brief called for with a bundle-friendly CSS version.
 */

const TELEGRAM_URL = "https://t.me/WhiteHatSupportBot";
const WHATSAPP_URL = "https://wa.me/+17248352705";

interface Props {
  /** When provided, renders a caption label under the icon (desktop only). */
  label?: string;
}

export function LiveChatButton({ label }: Props = {}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [tawkOpen, setTawkOpen] = useState(false);
  const open = !!anchor;

  const openTawk = () => {
    setTawkOpen(true);
    setAnchor(null);
  };

  const trigger = (
    <Tooltip title="Live chat with support">
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Live chat"
        sx={{
            position: "relative",
            // Keep the button size stable — the pulse/ping live inside
            // an absolutely-positioned dot that doesn't affect layout.
            "& .live-dot": {
              position: "absolute",
              top: 8,
              right: 8,
              width: 9,
              height: 9,
              borderRadius: "50%",
              bgcolor: "primary.main",
              boxShadow: "0 0 0 0 rgba(38,211,154,0.7)",
              animation: "whb-live-pulse 1.6s ease-out infinite"
            },
            "& .live-ping": {
              position: "absolute",
              top: 6,
              right: 6,
              width: 13,
              height: 13,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: "primary.main",
              opacity: 0,
              animation: "whb-live-ping 2.4s cubic-bezier(0, 0, 0.2, 1) infinite"
            },
            "@keyframes whb-live-pulse": {
              "0%":   { transform: "scale(0.9)", boxShadow: "0 0 0 0 rgba(38,211,154,0.55)" },
              "70%":  { transform: "scale(1.05)", boxShadow: "0 0 0 8px rgba(38,211,154,0)" },
              "100%": { transform: "scale(0.9)",  boxShadow: "0 0 0 0 rgba(38,211,154,0)" }
            },
            "@keyframes whb-live-ping": {
              "0%":   { transform: "scale(0.6)", opacity: 0.8 },
              "80%":  { transform: "scale(1.8)", opacity: 0 },
              "100%": { transform: "scale(1.8)", opacity: 0 }
            },
            // Respect reduced-motion preferences
            "@media (prefers-reduced-motion: reduce)": {
              "& .live-dot, & .live-ping": { animation: "none" }
            }
          }}
        >
          <SupportAgentRounded />
          <Box className="live-ping" aria-hidden />
          <Box className="live-dot" aria-hidden />
        </IconButton>
      </Tooltip>
  );

  return (
    <>
      {label ? <LabeledAction label={label}>{trigger}</LabeledAction> : trigger}

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: [
              { borderRadius: 3, minWidth: 260, mt: 0.5 },
              mobileFullWidthMenuPaperSx
            ]
          }
        }}
      >
        <MenuItem onClick={openTawk} sx={{ py: 1.25 }}>
          <ListItemIcon sx={{ color: "primary.main" }}>
            <ChatBubbleOutlineRounded />
          </ListItemIcon>
          <ListItemText
            primary="In-app chat"
            secondary="Powered by Tawk.to"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => { window.open(TELEGRAM_URL, "_blank", "noopener,noreferrer"); setAnchor(null); }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon sx={{ color: "#229ED9" }}>
            <TelegramIcon />
          </ListItemIcon>
          <ListItemText
            primary="Chat on Telegram"
            secondary="@WhiteHatSupportBot"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => { window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer"); setAnchor(null); }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon sx={{ color: "#25D366" }}>
            <WhatsApp />
          </ListItemIcon>
          <ListItemText
            primary="Chat on WhatsApp"
            secondary="+1 (724) 835-2705"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: "0.75rem" }}
          />
        </MenuItem>
      </Menu>

      <TawkChatDrawer open={tawkOpen} onClose={() => setTawkOpen(false)} />
    </>
  );
}
